'use client';
import classNames from 'classnames';
import { AnimatePresence, m, Variants } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';
import { Fetcher } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { shallow } from 'zustand/shallow';

import { carComparisonDismissSnackbarAction } from 'src/car-comparison/helpers/carComparisonDismissSnackbarAction';
import { CAR_COMPARISON_MAX } from 'src/constants';
import { fetchVehicleDetail } from 'src/data/ProductApi/ProductApiVehicleDetail';
import { LazyMotionWrapper } from 'src/general/components/LazyMotionWrapper/LazyMotionWrapper';
import { ToasterService } from 'src/general/components/Toaster/ToasterService';
import { IconCaretUp } from 'src/general/Icons/IconCaretUp';
import { carComparisonStore } from 'src/stores/carComparisonStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { Button } from '../../../general/components/Button/Button';
import styles from './CarComparisonBanner.module.scss';
import { CarComparisonBannerThumbnails } from './CarComparisonBannerThumbnails';
import { CarComparisonBannerTile } from './CarComparisonBannerTile';

type Props = {};

const vehicleFetcher: Fetcher<ProductDetailsPayload, [string, string]> = ([_path, sku]) => fetchVehicleDetail(sku);

const collapseVariant: Variants = {
  open: { opacity: 1, height: 'auto' },
  collapsed: { opacity: 0, height: 0 },
};
const easeOutQuad = [0.22, 1, 0.36, 1] as const;

const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 960;

export const CarComparisonBanner: FC<Props> = () => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { skus, removeAllSkus } = carComparisonStore(
    (state) => ({
      skus: state.skus,
      removeAllSkus: state.removeAllSkus,
    }),
    shallow,
  );
  const vehicleSwr1 = useSWRImmutable(skus.length >= 1 ? [`vehicles`, skus[0]] : null, vehicleFetcher);
  const vehicleSwr2 = useSWRImmutable(skus.length >= 2 ? [`vehicles`, skus[1]] : null, vehicleFetcher);
  const previousSkusLength = useRef<number>(skus.length);
  const canCompare = skus.length >= 2;

  const onRemoveAll = () => {
    pushToDataLayer({
      event: `car_comparison-remove_all`,
    });
    removeAllSkus();
  };

  /** Automatically set the open state of the banner depending on the size of the screen */
  useEffect(() => {
    if (isDesktop()) {
      setExpanded(true);
    }
  }, []);

  useEffect(() => {
    // If the user has added enough cars to the car comparison, expand the banner
    if (skus.length === CAR_COMPARISON_MAX && previousSkusLength.current < skus.length) {
      setExpanded(true);
    }
    // If the user has removed all the skus and is on mobile, shrink the banner
    if (expanded && skus.length === 0 && !isDesktop()) {
      setExpanded(false);
    }

    previousSkusLength.current = skus.length;
  }, [skus, expanded]);

  return (
    <LazyMotionWrapper>
      <AnimatePresence initial={false}>
        {skus.length > 0 && (
          <m.div
            className={classNames(styles.root, 'carComparisonBanner', {
              'carComparisonBanner--expanded': expanded,
            })}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={collapseVariant}
            transition={{ duration: 0.5, ease: easeOutQuad }}
          >
            <aside className={classNames(styles.container, 'c-se--white', 'c-se--default')}>
              <header className={styles.header}>
                <span className="c-fs-h6 c-fw-bold">Cars to compare</span>
                <span
                  className={classNames(styles.counter, 'c-fs-caption', 'c-fw-bold')}
                  data-testid="CompareCar-counter"
                >
                  {skus.length}/{CAR_COMPARISON_MAX}
                </span>
                <span className={styles.thumbnailsContainer}>
                  <CarComparisonBannerThumbnails vehicle1={vehicleSwr1} vehicle2={vehicleSwr2} />
                </span>
                <button
                  title="Toggle car comparison"
                  onClick={() => setExpanded((value) => !value)}
                  className={styles.bannerToggle}
                >
                  <span className={styles.bannerToggleIcon}>
                    <IconCaretUp />
                  </span>
                </button>
              </header>
              <AnimatePresence initial={false}>
                {expanded && (
                  <m.div
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={collapseVariant}
                    transition={{ duration: 0.3, ease: easeOutQuad }}
                    className={styles.content}
                  >
                    <div className={styles.carsWrapper}>
                      <CarComparisonBannerTile vehicle={vehicleSwr1} sku={skus[0] ?? undefined} />
                      <CarComparisonBannerTile vehicle={vehicleSwr2} sku={skus[1] ?? undefined} />
                    </div>
                    <footer className={styles.footer}>
                      <Button
                        variant="primary"
                        size="xsmall"
                        href={canCompare ? `/product-compare/${skus[0]}/${skus[1]}` : undefined}
                        onClick={
                          canCompare
                            ? undefined
                            : () => {
                                const errorMessage =
                                  'You’ve only selected 1 car to compare. Choose another car to compare with.';
                                pushToDataLayer({
                                  event: `car_comparison-error`,
                                  errorMessage,
                                });
                                ToasterService.error({
                                  message: errorMessage,
                                  action: {
                                    type: 'custom',
                                    actionComponent: carComparisonDismissSnackbarAction,
                                  },
                                });
                              }
                        }
                        data-testid="CompareComponent-Compare-Btn"
                      >
                        Compare
                      </Button>
                      <Button
                        variant="tertiary"
                        size="xsmall"
                        onClick={onRemoveAll}
                        data-testid="CompareComponent-ClearAll-Btn"
                      >
                        Clear all
                      </Button>
                    </footer>
                  </m.div>
                )}
              </AnimatePresence>
            </aside>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotionWrapper>
  );
};
